import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

import { STATUS_CHIP_BASE_CLASS, STATUS_CHIP_SIZE_STYLE } from './status-chip-style';

/** Hex values for the In Stock variant — not in the Tailwind token set,
 *  so applied via inline style (same pattern as ScoreChip). */
const IN_STOCK_BG = '#E8F5E9';
const IN_STOCK_TEXT = '#5B5B7A';

export type StockStatus = 'inStock' | 'outOfStock';

interface Props {
  /** Controls which visual variant is rendered. */
  status: StockStatus;
  className?: string;
  /** `data-testid` for the chip (default `stock-availability-chip`). */
  testId?: string;
}

/** Pill chip that surfaces product stock availability.
 *  Green pill for In Stock, destructive-bordered pill for Out of Stock. */
export function StockAvailabilityChip({
  status,
  className,
  testId = 'stock-availability-chip',
}: Props) {
  const { t } = useTranslation('common');
  const isInStock = status === 'inStock';

  return (
    <span
      data-testid={testId}
      data-status={status}
      className={cn(
        STATUS_CHIP_BASE_CLASS,
        isInStock ? '' : 'bg-background border-destructive text-destructive border',
        className,
      )}
      style={{
        ...STATUS_CHIP_SIZE_STYLE,
        ...(isInStock && { backgroundColor: IN_STOCK_BG, color: IN_STOCK_TEXT }),
      }}
    >
      {isInStock && <CircleCheck className="text-success h-4 w-4 shrink-0" aria-hidden="true" />}
      {isInStock ? t('stockAvailability.inStock') : t('stockAvailability.outOfStock')}
    </span>
  );
}
